/**
 * Slider Dots Component
 * Standalone JavaScript for slider dots navigation
 * Compatible with Shopify themes and slider components
 */

// Utility function for debouncing
function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction() {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

// Slider Dots Web Component
if (!customElements.get('slider-dots')) {
  customElements.define(
    'slider-dots',
    class SliderDots extends HTMLElement {
      constructor() {
        super();
        this.state = {
          currentIndex: 0,
          totalSlides: 0,
          isInitialized: false,
        };
        this.elements = {};
        this.boundMethods = {};
      }

      connectedCallback() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        this.cleanup();
      }

      init() {
        try {
          this.findElements();
          this.bindMethods();
          this.setupEventListeners();
          this.initializeState();
          this.state.isInitialized = true;

          // Dispatch initialization event
          this.dispatchEvent(
            new CustomEvent('dotsInitialized', {
              detail: { totalSlides: this.state.totalSlides },
            })
          );
        } catch (error) {
          console.error('SliderDots initialization error:', error);
        }
      }

      findElements() {
        // Find dots
        this.elements.dots = this.querySelectorAll('.slider-dot');
        this.elements.counter = this.querySelector('.slider-counter--current');
        this.elements.total = this.querySelector('.slider-counter--total');

        // Find associated slider
        this.elements.sliderComponent =
          this.closest('slider-component') ||
          this.parentElement.querySelector('slider-component') ||
          document.querySelector('slider-component');

        // Find media gallery
        this.elements.mediaGallery =
          this.closest('media-gallery') ||
          this.parentElement.querySelector('media-gallery') ||
          document.querySelector('media-gallery');

        // Find slider container
        if (this.elements.sliderComponent) {
          this.elements.slider =
            this.elements.sliderComponent.querySelector('.slider') ||
            this.elements.sliderComponent.querySelector('[class*="slider"]');
          this.elements.slides = this.elements.slider?.querySelectorAll('.slider__slide') || [];
        }

        // Find media slides if in media gallery
        if (this.elements.mediaGallery) {
          const viewer = this.elements.mediaGallery.querySelector('[id^="GalleryViewer"]');
          if (viewer) {
            this.elements.mediaSlides = viewer.querySelectorAll('li[data-media-id]') || [];
          }
        }

        // Set total slides count
        this.state.totalSlides =
          this.elements.slides.length || this.elements.mediaSlides?.length || this.elements.dots.length;
      }

      bindMethods() {
        this.boundMethods.handleDotClick = this.handleDotClick.bind(this);
        this.boundMethods.handleSliderScroll = debounce(this.handleSliderScroll.bind(this), 100);
        this.boundMethods.handleSlideChanged = this.handleSlideChanged.bind(this);
        this.boundMethods.handleMediaChanged = this.handleMediaChanged.bind(this);
        this.boundMethods.handleKeydown = this.handleKeydown.bind(this);
      }

      setupEventListeners() {
        // Dot click events - ensure we capture all dots
        this.elements.dots.forEach((dot, index) => {
          // Remove any existing listeners first
          dot.removeEventListener('click', this.boundMethods.handleDotClick);
          dot.removeEventListener('keydown', this.boundMethods.handleKeydown);

          // Add new listeners
          dot.addEventListener('click', (e) => {
            this.boundMethods.handleDotClick(e, index);
          });
          dot.addEventListener('keydown', this.boundMethods.handleKeydown);
        });

        // Slider scroll events
        if (this.elements.slider) {
          this.elements.slider.addEventListener('scroll', this.boundMethods.handleSliderScroll);
        }

        // Slider component events
        if (this.elements.sliderComponent) {
          this.elements.sliderComponent.addEventListener('slideChanged', this.boundMethods.handleSlideChanged);
        }

        // Media gallery events
        if (this.elements.mediaGallery) {
          this.elements.mediaGallery.addEventListener('mediaChanged', this.boundMethods.handleMediaChanged);
        }

        // Listen for custom events from other components
        document.addEventListener('sliderUpdate', this.boundMethods.handleSlideChanged);
      }

      initializeState() {
        // Find initially active slide
        let activeIndex = 0;

        // Check for active dot first (from your HTML structure)
        const activeDot = Array.from(this.elements.dots).findIndex(
          (dot) => dot.getAttribute('aria-current') === 'true' || dot.classList.contains('slider-dot--active')
        );
        if (activeDot !== -1) activeIndex = activeDot;

        // Check for active media in gallery
        if (this.elements.mediaSlides) {
          const activeMedia = Array.from(this.elements.mediaSlides).findIndex((slide) =>
            slide.classList.contains('is-active')
          );
          if (activeMedia !== -1) activeIndex = activeMedia;
        }

        // Check for active slide in slider
        if (this.elements.slides.length && !this.elements.mediaSlides && activeDot === -1) {
          const activeSlide = Array.from(this.elements.slides).findIndex(
            (slide) => slide.classList.contains('is-active') || slide.hasAttribute('aria-current')
          );
          if (activeSlide !== -1) activeIndex = activeSlide;
        }

        this.updateActiveState(activeIndex);
      }

      handleDotClick(event, index) {
        event.preventDefault();
        event.stopPropagation();

        if (index === this.state.currentIndex) return;

        this.goToSlide(index);
      }

      handleKeydown(event) {
        const index = Array.from(this.elements.dots).indexOf(event.target);

        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            this.previous();
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            event.preventDefault();
            this.next();
            break;
          case 'Home':
            event.preventDefault();
            this.goToSlide(0);
            break;
          case 'End':
            event.preventDefault();
            this.goToSlide(this.state.totalSlides - 1);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.goToSlide(index);
            break;
        }
      }

      handleSliderScroll() {
        if (!this.elements.slider || !this.elements.slides.length) return;

        try {
          const slideWidth = this.elements.slides[0]?.offsetWidth || 0;
          if (slideWidth === 0) return;

          const scrollLeft = this.elements.slider.scrollLeft;
          const currentIndex = Math.round(scrollLeft / slideWidth);

          if (currentIndex !== this.state.currentIndex && currentIndex < this.state.totalSlides) {
            this.updateActiveState(currentIndex);
          }
        } catch (error) {
          console.error('Slider scroll handler error:', error);
        }
      }

      handleSlideChanged(event) {
        if (!event.detail) return;

        let newIndex = this.state.currentIndex;

        if (typeof event.detail.currentIndex === 'number') {
          newIndex = event.detail.currentIndex;
        } else if (event.detail.currentElement) {
          // Find index of current element
          const slides = this.elements.slides.length ? this.elements.slides : this.elements.mediaSlides;
          if (slides) {
            newIndex = Array.from(slides).indexOf(event.detail.currentElement);
          }
        }

        if (newIndex !== -1 && newIndex !== this.state.currentIndex) {
          this.updateActiveState(newIndex);
        }
      }

      handleMediaChanged(event) {
        if (!event.detail || typeof event.detail.slideIndex !== 'number') return;

        this.updateActiveState(event.detail.slideIndex);
      }

      goToSlide(index) {
        if (index < 0 || index >= this.state.totalSlides || index === this.state.currentIndex) return;

        try {
          // Handle media gallery navigation
          if (this.elements.mediaGallery && this.elements.mediaSlides) {
            const targetSlide = this.elements.mediaSlides[index];
            if (targetSlide && targetSlide.dataset.mediaId) {
              // Use media gallery's setActiveMedia method
              if (typeof this.elements.mediaGallery.setActiveMedia === 'function') {
                this.elements.mediaGallery.setActiveMedia(targetSlide.dataset.mediaId, false);
              }
            }
          }
          // Handle regular slider navigation
          else if (this.elements.slider && this.elements.slides.length) {
            const targetSlide = this.elements.slides[index];
            if (targetSlide) {
              const slideWidth = targetSlide.offsetWidth;
              this.elements.slider.scrollTo({
                left: slideWidth * index,
                behavior: 'smooth',
              });
            }
          }

          // Dispatch slide change event
          this.dispatchSlideChangeEvent(index);

          // Update state
          this.updateActiveState(index);
        } catch (error) {
          console.error('Go to slide error:', error);
        }
      }

      updateActiveState(newIndex) {
        if (newIndex < 0 || newIndex >= this.state.totalSlides) return;

        const previousIndex = this.state.currentIndex;
        this.state.currentIndex = newIndex;

        // Update dots - ensure we handle string values correctly
        this.elements.dots.forEach((dot, index) => {
          const isActive = index === newIndex;
          dot.classList.toggle('slider-dot--active', isActive);

          // Set aria-current as string to match your Liquid template
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');

          // Update aria-label
          const slideNumber = index + 1;
          if (isActive) {
            dot.setAttribute('aria-label', `Current slide ${slideNumber} of ${this.state.totalSlides}`);
          } else {
            dot.setAttribute('aria-label', `Go to slide ${slideNumber} of ${this.state.totalSlides}`);
          }
        });

        // Update counter
        if (this.elements.counter) {
          this.elements.counter.textContent = newIndex + 1;
        }

        // Dispatch state change event
        this.dispatchEvent(
          new CustomEvent('dotStateChanged', {
            detail: {
              currentIndex: newIndex,
              previousIndex: previousIndex,
              totalSlides: this.state.totalSlides,
            },
          })
        );
      }

      dispatchSlideChangeEvent(index) {
        const targetElement = this.elements.slides?.[index] || this.elements.mediaSlides?.[index];

        const eventDetail = {
          currentIndex: index,
          previousIndex: this.state.currentIndex,
          currentElement: targetElement,
        };

        // Dispatch to slider component
        if (this.elements.sliderComponent) {
          this.elements.sliderComponent.dispatchEvent(new CustomEvent('slideChanged', { detail: eventDetail }));
        }

        // Dispatch to media gallery
        if (this.elements.mediaGallery && targetElement?.dataset.mediaId) {
          this.elements.mediaGallery.dispatchEvent(
            new CustomEvent('mediaChanged', {
              detail: {
                slideIndex: index,
                mediaId: targetElement.dataset.mediaId,
              },
            })
          );
        }

        // Global event
        document.dispatchEvent(new CustomEvent('sliderDotsChanged', { detail: eventDetail }));
      }

      // Public API methods
      next() {
        const nextIndex = (this.state.currentIndex + 1) % this.state.totalSlides;
        this.goToSlide(nextIndex);
      }

      previous() {
        const prevIndex = this.state.currentIndex === 0 ? this.state.totalSlides - 1 : this.state.currentIndex - 1;
        this.goToSlide(prevIndex);
      }

      goTo(index) {
        this.goToSlide(index);
      }

      getCurrentIndex() {
        return this.state.currentIndex;
      }

      getTotalSlides() {
        return this.state.totalSlides;
      }

      isInitialized() {
        return this.state.isInitialized;
      }

      // Update total slides count (useful for dynamic content)
      updateSlideCount(newCount) {
        if (newCount > 0 && newCount !== this.state.totalSlides) {
          this.state.totalSlides = newCount;
          if (this.elements.total) {
            this.elements.total.textContent = newCount;
          }
          // Reset to first slide if current index is out of bounds
          if (this.state.currentIndex >= newCount) {
            this.updateActiveState(0);
          }
        }
      }

      cleanup() {
        // Remove event listeners
        if (this.elements.slider) {
          this.elements.slider.removeEventListener('scroll', this.boundMethods.handleSliderScroll);
        }

        if (this.elements.sliderComponent) {
          this.elements.sliderComponent.removeEventListener('slideChanged', this.boundMethods.handleSlideChanged);
        }

        if (this.elements.mediaGallery) {
          this.elements.mediaGallery.removeEventListener('mediaChanged', this.boundMethods.handleMediaChanged);
        }

        document.removeEventListener('sliderUpdate', this.boundMethods.handleSlideChanged);

        this.elements.dots.forEach((dot) => {
          dot.removeEventListener('click', this.boundMethods.handleDotClick);
          dot.removeEventListener('keydown', this.boundMethods.handleKeydown);
        });

        // Clear references
        this.elements = {};
        this.boundMethods = {};
        this.state.isInitialized = false;
      }
    }
  );
}
